'use client';
import { useAppContext } from '@/context/app';
import { useMiniCartInfo } from '@/context/mini-cart';
import useIsMobile from '@/hooks/use-is-mobile';
import { useIsMobileSsr } from '@/hooks/use-is-mobile-ssr';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import MiniCartLazyLoad from '../MiniCart/mini-cart-lazy-load';
import HeaderToolbar from './headerToolbar';
import NavBar from './nav-bar';
import NavMenu from './nav-menu';
import NavTrigger from './navTrigger';

const HEADER_ID = 'aw-app-header';

import dynamic from 'next/dynamic';

const Navigation = dynamic(() => import('../Navigation/navigation'), { ssr: false });

const HeaderClient = props => {
  const { announcements, colorMenus, scarfProducts } = props;
  const pathname = usePathname();
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const [showNoItemDrawer, setShowNoItemDrawer] = useState(false);
  const [lastAddToCartTimestamp, setLastAddToCartTimestamp] = useState(() => new Date().toString());
  const [isForAddToCartOk, setIsForAddToCartOk] = useState(false);
  const [
    { headerOnTop, headerToolbarHeight },
    {
      actions: { toggleHeaderOnTop },
    },
  ] = useAppContext();
  const { isDesktop } = useIsMobileSsr();
  const isMobile = useIsMobile();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navTriggerRef = useRef(null);
  const shouldResetHeaderOnTopRef = useRef(false);

  const prevPropsRef = useRef({
    miniCartOpen,
  });

  if (prevPropsRef.current.miniCartOpen !== miniCartOpen) {
    prevPropsRef.current.miniCartOpen = miniCartOpen;

    if (!miniCartOpen) {
      setIsForAddToCartOk(false);
    }
  }

  const { setShouldQueryMiniCart } = useMiniCartInfo();
  const getShouldResetHeaderOnTop = useCallback(() => shouldResetHeaderOnTopRef.current, []);
  const setShouldResetHeaderOnTop = useCallback(nextState => (shouldResetHeaderOnTopRef.current = nextState), []);

  useEffect(() => {
    const scrollAction = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      setMiniCartOpen(true);
      setShouldQueryMiniCart(true);
      setLastAddToCartTimestamp(Date.now().toString());
      setIsForAddToCartOk(true);
    };

    document.addEventListener('CART_ADD_ITEM', scrollAction);

    return () => {
      document.removeEventListener('CART_ADD_ITEM', scrollAction);
    };
  }, [setShouldQueryMiniCart]);

  const getNavTriggerElement = useCallback(() => navTriggerRef.current, []);

  const handleTriggerSetMobileMenu = nextState => {
    const nextOpen = typeof nextState === 'function' ? nextState(mobileMenuVisible) : nextState;

    if (nextOpen) {
      toggleHeaderOnTop(true);
      setShouldResetHeaderOnTop(true);
    }

    setMobileMenuVisible(nextOpen);
  };

  const isWhoIsYourCharacterGame = pathname === '/who-is-your-character';

  if (pathname === '/checkout') {
    return (
      <header
        className={clsx(
          ' h-[60px] min-h-0 flex-none lg_h-[105px] ',
          'flex items-center justify-center',
          'border-b-1 border-solid border-E7DED5',
          'sticky left-0 top-0 z-[200]',
          'bg-white',
        )}
      >
        <Link href='/'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='151'
            height='34'
            fill='none'
            viewBox='0 0 151 34'
            className='lg_hidden'
          >
            <g
              fillRule='evenodd'
              clipPath='url(#clip0_13036_33788)'
              clipRule='evenodd'
            >
              <path
                fill='#BE9983'
                d='M27.345 24.456c.653-.061 1.594-.553 2.065-1.091.516-.584.76-1.322.091-1.83-.532-.399-1.367-.06-1.731.293-.425.415-.683 1.322-1.731 1.383-1.2.077-1.944-1.03-2.278-2.813-.015-.077-.03-.169-.046-.261-.167-1.06-.197-2.337-.09-3.736.03-.492.09-1.106.166-1.783.061-.507.213-1.522.41-3.028-.607.17-1.169.384-1.67.646-.182.107-.38.23-.607.368l-.486.339q-.137 1.106-.274 2.352c-.5.046-4.525.046-5.892 0 1.792-2.767 4.07-6.272 7.532-10.93-.243.984-.562 2.752-.926 5.319.729-.338 1.534-.615 2.445-.861l.273-.077c.122-.907.258-1.921.41-3.028.243-1.645.456-2.967.547-3.582.136-.722.303-1.46.486-2.167-1.564 1.352-3.508 1.276-6.014 1.46-4.935.415-7.866 2.998-7.866 6.395 0 1.199.502 1.921 1.367 1.921.653 0 1.109-.584 1.109-1.076 0-.66-.532-1.276-.532-1.998 0-.892.577-1.922 1.443-2.613 1.594-1.276 4.07-1.353 7.425-1.66C16.244 10.053 12.235 19.03 7.18 24.164c-1.033 1.046-1.974 1.691-3.007 1.691-.729 0-1.64-.353-2.126-.353-.729 0-1.108.492-1.108 1.106 0 .923.759 1.43 2.171 1.43 3.386 0 6.727-4.381 11.328-11.498 1.807-.031 5.77 0 6.773 0v.492c.06 1.475.38 3.074.926 4.212 1.321 2.751 3.052 3.412 5.209 3.212'
              />
              <path
                fill='#BE9983'
                d='M16.883 17.203c-.076.415-.121.83-.121 1.2 0 1.198.531 2.351 1.442 2.351.653 0 1.033-.538 1.033-1.153 0-.645-.744-1.291-.85-2.383zM17.688 15.082h1.472c.669-1.26 1.747-2.49 3.098-3.213 1.823-.968 3.888-1.214 3.888-1.214s-.456 1.46-.82 3.474c-.44 2.429-.79 4.98-.79 6.764.152.446.364.922.577 1.183.35.415.638.477.835.477h.076c.258-.015.425-.123.623-.323.152-.138.334-.384.5-.615 0-.23.016-.445.031-.676.182-2.644.395-5.134 1.048-7.932.638-2.69 1.625-5.273 1.625-5.273s-1.002.738-2.126 1.015c-1.64.4-4.935 1-7.456 2.86-1.124.845-1.99 2.213-2.581 3.473M51.596 5.414c-.713 0-1.29.507-1.29 1.2 0 .768.804 1.613.804 3.35 0 2.383-1.488 5.196-3.917 8.9-3.053 4.581-5.786 7.625-7.502 9.823.03-4.335.045-9.13.364-13.035.32-3.905.957-6.933.957-6.933s-2.293 1.691-4.009 4.274c-1.959 2.92-3.872 6.563-5.527 9.407-2.05 3.551-3.72 6.118-3.72 6.118s-.274-2.106-.456-3.366c-.213 0-.425.015-.623.015a5.4 5.4 0 0 1-1.974-.354c.213 3.229.592 6.165.987 8.64 7.578-9.316 10.098-16.787 12.3-19.569a54 54 0 0 0-.759 7.548l.182 11.529c3.007-2.89 5.528-6.134 8.914-10.638 4.525-6.04 6.955-10.591 6.955-13.758-.015-1.983-.623-3.15-1.686-3.15'
              />
              <path
                fill='#403732'
                d='M67.235 19c-.016 0-.016 0-.03-.016a5 5 0 0 0-.487-.062c-.288-.015-.638-.03-.956-.03.136-.031.258-.062.38-.093 1.199-.322 1.913-1.014 1.913-2.152 0-1.829-1.595-2.444-4.268-2.444h-1.943v.139c.622.03.744.215.744.937v8.609c0 .707-.122.906-.744.937v.123h4.1c2.627 0 4.282-.922 4.282-3.028-.015-1.691-1.093-2.644-2.992-2.92m-.228-2.337c0 .814-.258 1.506-1.154 1.89-.456.2-1.063.323-1.898.354h-.426v-4.504c2.84 0 3.478.953 3.478 2.26m-1.321 8.116h-2.142v-5.672h2.126c2.84 0 3.493 1.337 3.493 2.844s-.744 2.828-3.477 2.828M86.32 24.934c-.987-.169-1.018-1.783-1.504-3.167-.455-1.276-1.442-1.737-2.43-1.89 1.93-.246 2.947-1.169 2.947-2.767 0-1.876-1.382-2.829-4.024-2.829h-3.28v.139c.562.03.683.23.683.906v8.624c0 .723-.121.907-.775.938v.123h2.445v-.108c-.577-.03-.698-.246-.698-.922v-4.043h.957c2.004 0 2.626 0 3.234 2.414.47 1.829 1.048 2.69 2.232 2.782.152.015.304 0 .44-.03v-.17a.6.6 0 0 1-.227 0m-6.621-5.18V14.48h1.367c2.581 0 3.234 1.23 3.234 2.644s-.653 2.644-3.234 2.644h-1.367zM94.613 14.435c.73 0 .866.17.866.938v8.608c0 .753-.137.923-.866.938v.123h2.703v-.123c-.729 0-.865-.184-.865-.938v-8.608c0-.769.136-.922.865-.938v-.138h-2.703zM109.934 14.297h-4.586v.138c.713.016.85.17.85.938v8.608c0 .753-.137.923-.85.938v.123h4.586c3.781 0 5.876-2.414 5.876-5.38 0-2.967-2.095-5.365-5.876-5.365m-.274 10.56h-2.49V14.481h2.49c3.614 0 5.087 2.368 5.087 5.18 0 2.814-1.473 5.196-5.087 5.196M131.283 23.981l-3.781-9.038-.273-.646h-.182l-.426 1.122-3.264 8.562c-.258.646-.501.938-1.185.938h-.258v.123h2.566v-.123h-.303c-.775 0-.942-.17-.653-.938l.82-2.121.152-.4.212-.569.015-.03.426-1.122h3.295l.516 1.214.137.323.076.184.167.4.88 2.121c.319.738.365.938-.41.938h-.303v.123h3.28v-.123h-.167c-.775 0-1.003-.184-1.337-.938m-6.074-4.38 1.504-3.967.182.415.835 1.983.668 1.568zM145.591 23.643c0 .784-.167 1.214-.926 1.214h-2.582v-9.484c0-.769.137-.922.85-.938v-.138h-2.703v.138c.729.016.866.17.866.938v8.608c0 .753-.137.923-.866.938v.123h5.482v-1.537h-.121z'
              />
            </g>
            <defs>
              <clipPath id='clip0_13036_33788'>
                <path
                  fill='#fff'
                  d='M.938 0h150v33.45h-150z'
                />
              </clipPath>
            </defs>
          </svg>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='278'
            height='65'
            fill='none'
            viewBox='0 0 278 65'
            className='hidden lg_inline'
          >
            <path
              fill='#BE9983'
              fillRule='evenodd'
              d='M51.026 46.853c1.25-.118 3.05-1.059 3.951-2.088.988-1.118 1.453-2.53.174-3.5-1.017-.765-2.615-.118-3.312.559-.813.794-1.307 2.529-3.312 2.647-2.295.147-3.719-1.971-4.358-5.383-.03-.147-.058-.323-.087-.5-.32-2.03-.378-4.47-.175-7.147.059-.941.175-2.117.32-3.412.116-.97.407-2.911.784-5.794a16.5 16.5 0 0 0-3.196 1.236c-.348.206-.726.44-1.162.706l-.93.646a337 337 0 0 0-.523 4.5c-.958.089-8.658.089-11.273 0 3.429-5.294 7.787-12 14.411-20.911-.464 1.882-1.075 5.265-1.772 10.176 1.395-.647 2.935-1.176 4.678-1.647l.523-.147c.232-1.735.494-3.676.784-5.794.465-3.147.872-5.676 1.046-6.853.262-1.382.581-2.794.93-4.147-2.993 2.588-6.712 2.441-11.505 2.794-9.443.794-15.05 5.735-15.05 12.235 0 2.294.958 3.677 2.614 3.677 1.25 0 2.121-1.118 2.121-2.059 0-1.265-1.017-2.441-1.017-3.823 0-1.706 1.104-3.677 2.76-5 3.051-2.442 7.787-2.589 14.208-3.177-12.871 14.647-20.542 31.824-30.217 41.647-1.975 2-3.777 3.235-5.752 3.235-1.395 0-3.138-.676-4.068-.676-1.395 0-2.121.941-2.121 2.118 0 1.764 1.453 2.735 4.155 2.735 6.479 0 12.87-8.382 21.674-22 3.458-.059 11.041 0 12.959 0v.941c.116 2.824.726 5.882 1.772 8.059 2.528 5.265 5.84 6.53 9.966 6.147'
              clipRule='evenodd'
            />
            <path
              fill='#BE9983'
              fillRule='evenodd'
              d='M31.006 32.969a13 13 0 0 0-.233 2.294c0 2.294 1.017 4.5 2.76 4.5 1.25 0 1.976-1.03 1.976-2.206 0-1.235-1.423-2.47-1.627-4.559zM32.547 28.91h2.8179999999999996c1.279-2.411 3.341-4.764 5.927-6.147 3.487-1.853 7.438-2.323 7.438-2.323s-.871 2.794-1.569 6.647c-.842 4.647-1.51 9.53-1.51 12.941.29.853.697 1.765 1.103 2.265.669.794 1.22.912 1.599.912h.145c.494-.03.813-.236 1.191-.618.29-.265.64-.735.959-1.177 0-.44.029-.853.058-1.294.349-5.059.755-9.823 2.005-15.176 1.22-5.147 3.108-10.088 3.108-10.088s-1.917 1.411-4.067 1.94c-3.138.765-9.443 1.912-14.266 5.471-2.15 1.618-3.806 4.236-4.94 6.647M97.424 10.414c-1.365 0-2.47.97-2.47 2.294 0 1.47 1.54 3.088 1.54 6.412 0 4.559-2.847 9.941-7.496 17.03-5.84 8.764-11.07 14.588-14.353 18.794.059-8.295.088-17.471.698-24.942.61-7.47 1.83-13.264 1.83-13.264s-4.387 3.235-7.67 8.176c-3.748 5.588-7.409 12.559-10.576 18-3.922 6.794-7.118 11.706-7.118 11.706s-.523-4.03-.872-6.441c-.407 0-.813.03-1.191.03-1.395 0-2.644-.236-3.777-.677.407 6.176 1.133 11.794 1.888 16.53C62.356 46.237 67.18 31.943 71.391 26.62A103.5 103.5 0 0 0 69.94 41.06l.348 22.059c5.753-5.53 10.576-11.735 17.055-20.353 8.659-11.559 13.307-20.265 13.307-26.324-.029-3.794-1.191-6.029-3.225-6.029'
              clipRule='evenodd'
            />
            <path
              fill='#403732'
              fillRule='evenodd'
              d='M127.346 36.41c-.029 0-.029 0-.059-.029-.29-.058-.61-.088-.929-.117a36 36 0 0 0-1.831-.059c.262-.059.494-.118.727-.176 2.295-.618 3.661-1.942 3.661-4.118 0-3.5-3.051-4.677-8.165-4.677h-3.719v.265c1.191.059 1.424.412 1.424 1.794v16.47c0 1.354-.233 1.736-1.424 1.795v.235h7.845c5.026 0 8.193-1.764 8.193-5.794-.029-3.235-2.092-5.059-5.723-5.588m-.436-4.47c0 1.56-.494 2.883-2.208 3.618-.872.382-2.034.618-3.632.676h-.814v-8.617c5.433 0 6.654 1.823 6.654 4.323m-2.528 15.53h-4.097V36.617h4.068c5.433 0 6.683 2.559 6.683 5.44 0 2.883-1.424 5.413-6.654 5.413M163.866 47.765c-1.888-.323-1.946-3.412-2.876-6.059-.872-2.44-2.76-3.323-4.649-3.617 3.69-.47 5.637-2.236 5.637-5.294 0-3.589-2.644-5.412-7.7-5.412h-6.276v.265c1.075.058 1.308.44 1.308 1.735v16.5c0 1.382-.233 1.735-1.482 1.794v.235h4.678v-.206c-1.104-.059-1.337-.47-1.337-1.764v-7.736H153c3.835 0 5.026 0 6.188 4.618.901 3.5 2.005 5.147 4.271 5.324.291.029.582 0 .843-.06v-.323c-.116.03-.291.03-.436 0m-12.668-9.912V27.765h2.615c4.94 0 6.189 2.353 6.189 5.059s-1.249 5.059-6.189 5.059h-2.615zM179.734 27.679c1.395 0 1.656.323 1.656 1.794v16.47c0 1.442-.261 1.765-1.656 1.795v.235h5.172v-.235c-1.395 0-1.656-.353-1.656-1.794V29.473c0-1.47.261-1.765 1.656-1.794v-.265h-5.172zM209.048 27.414h-8.775v.265c1.366.03 1.628.323 1.628 1.794v16.47c0 1.442-.262 1.765-1.628 1.795v.235h8.775c7.234 0 11.244-4.618 11.244-10.294s-4.01-10.265-11.244-10.265m-.523 20.206h-4.765V27.767h4.765c6.915 0 9.733 4.53 9.733 9.912s-2.818 9.94-9.733 9.94M249.895 45.944l-7.234-17.295-.523-1.235h-.349l-.813 2.147-6.247 16.383c-.494 1.235-.959 1.794-2.266 1.794h-.494v.235h4.91v-.235h-.581c-1.482 0-1.802-.324-1.249-1.794l1.568-4.06.291-.764.407-1.088.029-.06.813-2.146h6.305l.988 2.323.261.618.146.353.319.765 1.686 4.059c.61 1.411.697 1.794-.785 1.794h-.581v.235h6.276v-.235h-.32c-1.482 0-1.917-.353-2.557-1.794m-11.621-8.383 2.876-7.588.349.794 1.598 3.794 1.278 3zM277.268 45.296c0 1.5-.32 2.324-1.772 2.324h-4.94V29.473c0-1.47.262-1.765 1.627-1.794v-.265h-5.171v.265c1.394.03 1.656.323 1.656 1.794v16.47c0 1.442-.262 1.765-1.656 1.795v.235H277.5v-2.941h-.232z'
              clipRule='evenodd'
            />
          </svg>
        </Link>
      </header>
    );
  }

  return (
    <>
      <header
        className={clsx(
          ' sticky top-0 z-40 h-auto min-h-0 w-full flex-none  bg-white transition-all [transition-duration:400ms]',
        )}
        style={{
          transform: headerOnTop ? undefined : `translateY(-${headerToolbarHeight}px)`,
        }}
        id={HEADER_ID}
      >
        <HeaderToolbar
          announcements={isWhoIsYourCharacterGame ? null : announcements}
          miniCartOpen={miniCartOpen}
          setMiniCartOpen={setMiniCartOpen}
          setShowNoItemDrawer={setShowNoItemDrawer}
          headerId={HEADER_ID}
          getShouldResetHeaderOnTop={getShouldResetHeaderOnTop}
          setShouldResetHeaderOnTop={setShouldResetHeaderOnTop}
          navTrigger={
            isWhoIsYourCharacterGame ? null : (
              <NavTrigger
                setMobileMenuVisible={handleTriggerSetMobileMenu}
                mobileMenuVisible={mobileMenuVisible}
                ref={navTriggerRef}
              />
            )
          }
        />
        {isDesktop && !isWhoIsYourCharacterGame && (
          <NavBar
            navMenu={<NavMenu colorMenus={colorMenus} />}
            miniCartOpen={miniCartOpen}
            setMiniCartOpen={setMiniCartOpen}
            setShowNoItemDrawer={setShowNoItemDrawer}
          />
        )}
      </header>
      {isMobile && (
        <Navigation
          open={mobileMenuVisible}
          onOpenChange={setMobileMenuVisible}
          getNavTriggerElement={getNavTriggerElement}
        />
      )}
      <MiniCartLazyLoad
        isOpen={miniCartOpen}
        setIsOpen={setMiniCartOpen}
        showNoItemDrawer={showNoItemDrawer}
        setShowNoItemDrawer={setShowNoItemDrawer}
        lastAddToCartTimestamp={lastAddToCartTimestamp}
        isForAddToCartOk={isForAddToCartOk}
        scarfProducts={scarfProducts}
      />
    </>
  );
};

export default HeaderClient;
